/**
 * Gift Progress Bar with Auto-Add/Remove Functionality
 * Manages free gift products based on cart subtotal thresholds
 */

(function() {
  'use strict';

  class GiftProgressManager {
    constructor() {
      this.progressBar = null;
      this.currentThreshold = -1; // -1 = uninitialized; set correctly after first handleCartUpdate
      this.settings = {};
      this.isProcessing = false;
      this.updateTimer = null;
      this.ogMinDelay = 0;       // minimum delay enforced after OG offer interaction
      this.ogMinDelayTimer = null;
      this._ogSubscribed = false; // cached OG status; used to instantly hide bar on DOM insertion
      this._ownRefreshPending = false; // blocks the extra handleCartUpdate after our own section refresh
      this.init();
      
    }

    init() {
       // Covers empty-cart first-add AND section re-renders after gift add/remove.
      this.watchForBarChanges();

      // Instantly hide/show bar when OG sets/removes the subscribed attribute.
      this.watchOgOfferChanges();

      // Also listen for theme:cart:updated for qty changes (bar already in DOM).
      this.bindEvents();

      // If bar is already in DOM on page load (non-empty cart), run immediately.
      this.progressBar = document.querySelector('[data-gift-progress-bar]');
      if (this.progressBar) {
        this.loadSettings();
        this.scheduleUpdate(50);
      }
    }

    /**
     * The last caller wins — unless an OG offer was recently interacted with,
     * in which case shorter delays are blocked until the OG API call has had
     * time to complete (ogMinDelay guard).
     */
    scheduleUpdate(delay) {
      if (this.ogMinDelay > 0 && delay < this.ogMinDelay) return;
      clearTimeout(this.updateTimer);
      this.updateTimer = setTimeout(() => this.handleCartUpdate(), delay);
    }

    /**
     * inserted into the DOM at any depth (covers first-add from empty cart and
     * every swapInNewContent re-render).
     */
    watchForBarChanges() {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            const found = node.matches('[data-gift-progress-bar]')
              || node.querySelector('[data-gift-progress-bar]');
            if (found) {
              this.progressBar = document.querySelector('[data-gift-progress-bar]');
              if (this.progressBar) {
                // If OG was active in the last cycle OR is already active in the DOM,
                // hide immediately so the bar never flashes while the debounce + fetch run.
                if (this._ogSubscribed || this._isOgSubscribedInDOM()) {
                  this.progressBar.style.display = 'none';
                  this._ogSubscribed = true;
                }
                this.loadSettings();
                // 50ms: fires before the 100ms theme:cart:updated debounce,
                // but scheduleUpdate unifies both â€” the later call wins.
                this.scheduleUpdate(50);
              }
              return;
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * True when any og-offer element currently carries the subscribed attribute —
     * set by OG the moment the user selects a subscription option, before any
     * cart API call completes.
     */
    _isOgSubscribedInDOM() {
      return !!document.querySelector('og-offer[subscribed]');
    }

    /**
     * Watches for OG setting/removing the subscribed attribute on og-offer elements.
     * Hides the bar instantly on subscribe; triggers a full re-evaluate on unsubscribe.
     * No /cart.js fetch needed — reacts at attribute-mutation speed (~0 ms).
     */
    watchOgOfferChanges() {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName !== 'subscribed') continue;
          const target = mutation.target;
          if (target.tagName.toLowerCase() !== 'og-offer') continue;

          if (target.hasAttribute('subscribed')) {
            // OG just activated — hide bar immediately, no fetch required.
            this._ogSubscribed = true;
            const bar = document.querySelector('[data-gift-progress-bar]');
            if (bar) bar.style.display = 'none';
          } else {
            // OG removed — re-evaluate via full handleCartUpdate cycle.
            this._ogSubscribed = false;
            this.scheduleUpdate(100);
          }
        }
      });
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['subscribed'],
        subtree: true
      });
    }

    // ── Checkout spinner ──────────────────────────────────────────────────

    /** Show native spinner on checkout buttons while gifts are processing. */
    showCheckoutLoading() {
      document.querySelectorAll('cart-element .cart--button').forEach(btn => {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-busy', 'true');
      });
    }

    /** Remove spinner and re-enable checkout buttons. */
    hideCheckoutLoading() {
      document.querySelectorAll('cart-element .cart--button').forEach(btn => {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      });
    }

    getSettingsFromDOM() {
      const bar = document.querySelector('[data-gift-progress-bar]');
      if (!bar) return null;
      return {
        threshold1Min:      parseInt(bar.getAttribute('data-threshold-1-min'))      || 0,
        threshold1Max:      parseInt(bar.getAttribute('data-threshold-1-max'))      || 0,
        threshold2Value:    parseInt(bar.getAttribute('data-threshold-2-value'))    || 0,
        threshold3Value:    parseInt(bar.getAttribute('data-threshold-3-value'))    || 0,
        threshold1Enabled:  bar.getAttribute('data-threshold-1-enabled') === 'true',
        threshold2Enabled:  bar.getAttribute('data-threshold-2-enabled') === 'true',
        threshold3Enabled:  bar.getAttribute('data-threshold-3-enabled') === 'true',
        threshold2Title:    bar.getAttribute('data-threshold-2-title') || 'FREE 4-Piece Gift ($49 Value)',
        threshold3Title:    bar.getAttribute('data-threshold-3-title') || '4-Piece Gift ($75 Value)',
        threshold3NextLabel: bar.getAttribute('data-threshold-3-next-label') || '$75 Value Gift',
        threshold3ShortTitle: bar.getAttribute('data-threshold-3-short-title') || '4-Piece Gift',
        barMaxValue:        parseInt(bar.getAttribute('data-bar-max-value'))        || 40000,
        threshold1Products: this.parseProducts(bar.getAttribute('data-threshold-1-products')),
        threshold2Products: this.parseProducts(bar.getAttribute('data-threshold-2-products')),
        threshold3Products:     this.parseProducts(bar.getAttribute('data-threshold-3-products')),
        threshold3DoubleQtyVid: parseInt(bar.getAttribute('data-threshold-3-double-qty-vid')) || 0,
        threshold4Min:      parseInt(bar.getAttribute('data-threshold-4-min'))      || 0,
        threshold4Max:      parseInt(bar.getAttribute('data-threshold-4-max'))      || 0,
        threshold4Enabled:  bar.getAttribute('data-threshold-4-enabled') === 'true',
        threshold4Products: this.parseProducts(bar.getAttribute('data-threshold-4-products')),
        threshold5Min:      parseInt(bar.getAttribute('data-threshold-5-min'))      || 0,
        threshold5Enabled:  bar.getAttribute('data-threshold-5-enabled') === 'true',
        threshold5Products: this.parseProducts(bar.getAttribute('data-threshold-5-products')),
        threshold5DoubleQtyVid: parseInt(bar.getAttribute('data-threshold-5-double-qty-vid')) || 0,
        kitsInCart:        (bar.getAttribute('data-kits-in-cart') || '')
                              .split(',')
                              .map(id => parseInt(id))
                              .filter(id => !isNaN(id) && id > 0),
        // All configured kit product IDs (from product_list setting) â€” always complete, no API needed
        kitsProductIds:    (bar.getAttribute('data-kits-product-ids') || '')
                              .split(',')
                              .map(id => parseInt(id))
                              .filter(id => !isNaN(id) && id > 0),
        kitsCollectionHandle: bar.getAttribute('data-kits-collection-handle') || 'our-kits',
        complimentarySku:  (bar.getAttribute('data-complimentary-sku') || '').trim()
      };
    }

    loadSettings() {
      const settings = this.getSettingsFromDOM();
      if (settings) {
        this.settings = settings;
      }
    }

    parseProducts(jsonString) {
      try {
        if (!jsonString || jsonString === 'null') return [];
        // url_encode from Liquid â€” decode percent-encoding first
        const decoded = decodeURIComponent(jsonString);
        const products = JSON.parse(decoded);
        return Array.isArray(products) ? products : [];
      } catch (e) {
        console.error('Gift Progress Bar: Error parsing gift products:', e, 'raw:', jsonString ? jsonString.substring(0, 100) : 'empty');
        return [];
      }
    }

    bindEvents() {

      window.addEventListener('theme:cart:updated', () => this.syncCartEmptyState());

      // Debounced: actual gift add/remove API logic.
      window.addEventListener('theme:cart:updated', () => this.scheduleUpdate(50));

      // Re-evaluate gifts when the promo app signals a code was applied or removed.
      // This closes the race where handleCartUpdate runs before the app has set __skiiPromoActive.
      window.addEventListener('skii:promo:applied', () => this.scheduleUpdate(100));
      window.addEventListener('skii:promo:removed', () => this.scheduleUpdate(100));
  
      window.addEventListener('theme:drawer:right:opened', () => {
        this.syncCartEmptyState();
        this.scheduleUpdate(50);
      });
      
      const onOgOfferInteract = (e) => {
        if (e.target.closest && e.target.closest('og-offer')) {
          // Set a minimum delay so that theme:cart:updated (50 ms) cannot
          // fire our handler before OG's own cart API call has completed.
          this.ogMinDelay = 500;
          clearTimeout(this.ogMinDelayTimer);
          this.ogMinDelayTimer = setTimeout(() => { this.ogMinDelay = 0; }, 500);
          this.scheduleUpdate(500);
        }
      };
      // Only 'change' fires when the user actually picks a new option.
      // Removing 'click' prevents spurious scheduleUpdate(500) calls when the
      // user merely opens or closes the dropdown without changing the value.
      document.addEventListener('change', onOgOfferInteract);
    }

    /**
     * Immediately sets data-cart-empty="true" on .layout--viewport when the
     * cart body contains no non-gift items. Called synchronously on every
     * theme:cart:updated so the CSS hides the checkout button and item list
     * before the ~50 ms debounce fires.
     *
     * Also handles the Blockshop swapInImages() bug: when the incoming cart
     * body has zero items, swapInImages() returns false and the body is never
     * replaced via replaceWith(). Stale paid items and the stale progress bar
     * linger in the DOM. Because the bar has bar.style.display='block' set as
     * an inline style, it breaks out of the CSS display:none on .cart--body
     * and becomes visible again. We actively remove those stale nodes here.
     */
    syncCartEmptyState() {
      const body = document.querySelector('.cart--body');
      if (!body) return;
      const viewport = document.querySelector('.layout--viewport');
      const cartIsEmpty = viewport && viewport.getAttribute('data-cart-empty') === 'true';
      const paidItems = body.querySelectorAll('.cart--item:not([data-is-gift="true"])');

      if (paidItems.length === 0) {
        // No paid items: enforce empty state and strip stale gift rows / bar left by Blockshop's zero-item no-op re-render.
        body.querySelectorAll('.cart--item[data-is-gift="true"]').forEach(el => el.remove());
        const staleGiftBar = body.querySelector('[data-gift-progress-bar]');
        if (staleGiftBar) staleGiftBar.remove();
        if (viewport) viewport.setAttribute('data-cart-empty', 'true');
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.setAttribute('data-cart-count', '0');
        });
      } else if (cartIsEmpty) {
      
        paidItems.forEach(el => el.remove());
        body.querySelectorAll('.cart--item[data-is-gift="true"]').forEach(el => el.remove());
        const staleBar = body.querySelector('[data-gift-progress-bar]');
        if (staleBar) staleBar.remove();
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.setAttribute('data-cart-count', '0');
        });
      }
    }

    async handleCartUpdate() {
      // If already running, flag it â€” we'll re-run once the current pass finishes
      if (this.isProcessing) {
        this.pendingUpdate = true;
        return;
      }
      // Skip the single extra cycle Blockshop fires after our own refreshCartSections
      // swaps the section HTML. Sections are already current; no /cart.js fetch needed.
      if (this._ownRefreshPending) {
        this._ownRefreshPending = false;
        return;
      }
      this.isProcessing = true;
      this.pendingUpdate = false;

      // Capture drawer reference before try so finally can always remove the overlay
      // that after-pay-popup.js adds on OG subscription changes.
      const _drawerEl = document.querySelector('drawer-element');

      try {
        // Re-query bar â€” DOM may have been re-rendered
        this.progressBar = document.querySelector('[data-gift-progress-bar]');
        if (!this.progressBar) {
          // Bar absent = no paid items: remove orphan gifts using the SERVER cart (DOM node may already be stripped).
          const orphanCart = await this.fetchCart();
          await this.updateGiftProducts(0, false, orphanCart);
          this.isProcessing = false;
          return;
        }

        this.loadSettings();

        if (!this.settings || !this.settings.threshold2Value) {
          console.warn('Gift Progress Bar: Settings not ready yet, retrying...');
          this.isProcessing = false;
          setTimeout(() => this.handleCartUpdate(), 300);
          return;
        }


        const _paidInDOM = document.querySelectorAll(
          '.cart--body .cart--item:not([data-is-gift="true"])'
        ).length;
        const _viewport  = document.querySelector('.layout--viewport');
        const _cartEmpty = _viewport && _viewport.getAttribute('data-cart-empty') === 'true';
        const isStaleEmpty = _cartEmpty || _paidInDOM === 0;

        // Single /cart.js fetch for the entire update cycle.
        // Both ogSubscribed detection and gift-state diff use this same cart snapshot,
        // so the DOM is never used as source of truth for what gifts currently exist.
        const cart = await this.fetchCart();
        this._lastCart = cart; // cache for _isPromoActiveFromCart outside handleCartUpdate

        // When AWO discount is active, use original_line_price (pre-discount) for threshold
        // calculation so the 10% discount doesn't push subtotal below gift thresholds.
        // The theme's Liquid template uses final_line_price which is post-discount.
        let actualSubtotal;
        if (!isStaleEmpty && cart && this._isAwoActive(cart)) {
          actualSubtotal = this._getOriginalSubtotal(cart);
        } else {
          actualSubtotal = isStaleEmpty
            ? 0
            : (parseInt(this.progressBar.getAttribute('data-actual-subtotal')) || 0);
        }

        const targetThreshold = this.determineThreshold(actualSubtotal);

        // Trust DOM og-offer[subscribed] attribute over cart JSON — the cart
        // selling_plan lags behind the user's dropdown selection, which would
        // keep the progress bar hidden after switching to one-time purchase.
        const ogInDOM = document.querySelector('og-offer');
        const ogSubscribed = ogInDOM
          ? this._isOgSubscribedInDOM()
          : (cart ? this.isOgSubscribedFromCart(cart) : false);
        this._ogSubscribed = ogSubscribed; // cache for MutationObserver pre-hide

        // Sync header cart-count badge before gift diff runs.
        // If updateGiftProducts returns early (gifts already correct), refreshCartSections
        // is never called, so this is the only place the badge gets corrected after
        // Cart.addItems sets it to basket-sum (which excludes gifts bypassed via direct fetch).
        if (cart && typeof cart.item_count === 'number') {
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.setAttribute('data-cart-count', cart.item_count);
          });
        }

        if (!isStaleEmpty) {
          this.updateProgressBar(actualSubtotal, targetThreshold, ogSubscribed);
        }
        await this.updateGiftProducts(targetThreshold, ogSubscribed, cart);
        this.currentThreshold = targetThreshold;
      } catch (error) {
        console.error('Error updating cart gifts:', error);
      } finally {
        this.isProcessing = false;
        this.hideCheckoutLoading();
        // Safety: ensure gift-add flag is cleared even if updateGiftProducts threw.
        window.__skiiGiftAddInProgress = false;
        // Remove the overlay that after-pay-popup.js adds on OG subscription changes.
        // Guard: if after-pay-popup.js is still processing its OG refresh cycle
        // (__ogRefreshPending = true), keep the overlay on until it completes —
        // otherwise the cart would be visible before OG's change is fully reflected.
        if (!window.__ogRefreshPending && _drawerEl) _drawerEl.classList.remove('opacity-delay');
        // Re-run if a cart update arrived while we were processing
        if (this.pendingUpdate) {
          this.pendingUpdate = false;
          this.scheduleUpdate(50);
        }
      }
    }

    // Returns a Set of variant IDs for gift items currently visible in the drawer DOM.
    // Used only as a fallback when the cart JSON is unavailable.
    // Excludes app-managed promo gifts (those with data-promo-source) so the theme
    // never removes gifts owned by the PromoAutoGifts app.
    getGiftVariantsInDOM() {
      const variants = new Set();
      document.querySelectorAll('.cart--item[data-is-gift="true"]').forEach(el => {
        // Skip app-managed promo gifts — they have _promo_source set by the app.
        // DOM items don't expose line-item properties directly, so when the promo
        // app is active we conservatively skip ALL gift items to avoid removing
        // app-managed gifts by mistake.
        if (window.__skiiPromoActive === true || this._isPromoActiveFromCart(this._lastCart)) return;
        const vid = parseInt(el.getAttribute('data-variant-id'));
        if (vid) variants.add(vid);
      });
      return variants;
    }
    // OPTIMIZATION: Brief cache (300ms) prevents duplicate /cart.js fetches when
    // multiple events fire in rapid succession (e.g. theme:cart:updated + scheduleUpdate
    // both triggering within the same debounce window). Flow is unchanged — the first
    // call fetches fresh data, subsequent calls within 300ms reuse it.
    async fetchCart() {
      const now = Date.now();
      if (this._cartCache && (now - this._cartCacheTime) < 300) {
        return this._cartCache;
      }
      try {
        const r = await fetch('/cart.js');
        if (!r.ok) return null;
        const cart = await r.json();
        this._cartCache = cart;
        this._cartCacheTime = now;
        return cart;
      } catch (e) {
        console.error('Gift Progress Bar: fetchCart error:', e);
        return null;
      }
    }

    // Derives OG subscription status from a cart object already in memory — no extra fetch.
    isOgSubscribedFromCart(cart) {
      return (cart.items || []).some(item =>
        item.selling_plan_allocation != null &&
        !(item.properties && item.properties['_gift_product'] === 'true')
      );
    }

    // Detects NON-AWO promo activity from the cart JSON — independent of the __skiiPromoActive flag.
    // Returns true when the cart has a non-AWO discount code AND the promo-auto-gifts app config
    // element exists in the DOM. AWO codes are excluded because the theme's own threshold logic
    // handles AWO gifts (T2/T3), not the auto-gifts app.
    _isPromoActiveFromCart(cart) {
      if (!cart) return false;
      const codes = (cart.discount_codes || [])
        .map(dc => (dc && dc.code) || '')
        .filter(c => c);
      if (codes.length === 0) return false;
      // AWO codes are managed by the theme's own threshold gifts — skip them
      const hasNonAwoCodes = codes.some(c => !c.toUpperCase().startsWith('AWO'));
      if (!hasNonAwoCodes) return false;
      return !!document.getElementById('skii-promo-auto-gift-config');
    }

    // Returns true when any AWO-prefixed discount code is applied in the cart.
    // Used to hide the progress bar while keeping the theme's threshold gifts active.
    _isAwoActive(cart) {
      if (!cart) return false;
      return (cart.discount_codes || []).some(dc =>
        dc && dc.code && dc.code.toUpperCase().startsWith('AWO')
      );
    }

    // Calculates cart subtotal from original_line_price (pre-discount) using the cart JSON.
    // Excludes gift items and kit products — mirrors the Liquid template logic but uses
    // pre-discount prices so AWO's 10% doesn't reduce the threshold-qualifying subtotal.
    _getOriginalSubtotal(cart) {
      if (!cart || !cart.items) return 0;
      const kitsIds = new Set((this.settings.kitsProductIds || []).map(Number));
      let subtotal = 0;
      cart.items.forEach(item => {
        if (item.properties && item.properties['_gift_product'] === 'true') return;
        if (kitsIds.has(Number(item.product_id))) return;
        subtotal += item.original_line_price || item.line_price || 0;
      });
      return subtotal;
    }

    // Returns a Set of gift variant IDs from the cart JSON — authoritative source of truth.
    // Fixes the switch-back bug where stale DOM missed OG gifts still in the backend cart.
    // Excludes app-managed promo gifts (those with _promo_source: "skii_promo_app") so the
    // theme never removes gifts owned by the PromoAutoGifts app.
    getGiftVariantsFromCart(cart) {
      const variants = new Set();
      (cart.items || []).forEach(item => {
        if (item.properties && item.properties['_gift_product'] === 'true'
            && !(item.properties['_promo_source'] === 'skii_promo_app')) {
          variants.add(Number(item.variant_id));
        }
      });
      return variants;
    }

    async isOgSubscribed() {
      try {
        const r = await fetch('/cart.js');
        const cart = await r.json();
        return this.isOgSubscribedFromCart(cart);
      } catch (e) {
        return false;
      }
    }
    determineThreshold(subtotal) {
      const { threshold1Min, threshold2Value, threshold3Value,
              threshold1Enabled, threshold2Enabled, threshold3Enabled } = this.settings;

      if (threshold3Enabled && subtotal >= threshold3Value) {
        return 3;
      } else if (threshold2Enabled && subtotal >= threshold2Value) {
        return 2;
      } else if (threshold1Enabled && subtotal >= threshold1Min && subtotal < threshold2Value) {
        return 1;
      }
      return 0;
    }

    async updateGiftProducts(targetThreshold, ogSubscribed, cart) {
      const { threshold1Products, threshold2Products, threshold3Products,
              threshold4Min, threshold4Max, threshold4Enabled, threshold4Products,
              threshold5Min, threshold5Enabled, threshold5Products } = this.settings;

      // Use cart JSON as source of truth when available.
      // Falls back to DOM only if cart fetch failed (e.g. network error).
      const currentGiftVariants = cart
        ? this.getGiftVariantsFromCart(cart)
        : this.getGiftVariantsInDOM();

      // Helper: first variant ID for a product object
      const firstVariantId = p => p.variants && p.variants[0] ? p.variants[0].id : null;

      // ogSubscribed is passed in from handleCartUpdate (already fetched once)
      // ogAtT5: true when OG is active AND cart subtotal qualifies for T5.
      // Defined outside the if-block so the dqVid check below can use it.
      let ogAtT5 = false;
      let targetProducts;
      if (ogSubscribed) {
        // OG subscription active: apply T4/T5 gifts based on cart subtotal
        const bar = document.querySelector('[data-gift-progress-bar]');
        const subtotal = parseInt(bar?.getAttribute('data-actual-subtotal')) || 0;
        ogAtT5 = subtotal >= threshold5Min && threshold5Enabled;
        if (ogAtT5 && threshold5Products.length > 0) {
          targetProducts = threshold5Products;
        } else if (subtotal >= threshold4Min && subtotal <= threshold4Max && threshold4Enabled && threshold4Products.length > 0) {
          targetProducts = threshold4Products;
        } else {
          targetProducts = [];
        }
      } else {
        // One-time purchase: apply KCP/non-KCP gifts based on targetThreshold
        targetProducts =
          targetThreshold === 1 ? threshold1Products :
          targetThreshold === 2 ? threshold2Products :
          targetThreshold === 3 ? threshold3Products : [];
      }

      const targetVIds = new Set(
        targetProducts.map(firstVariantId).filter(Boolean)
      );

      // Double-qty product: T3 KCP (non-OG) or T5 OG $300 tier.
      // ogAtT5 flag avoids a broken reference comparison that fails when
      // threshold5Products is empty (double-qty-only T5 configuration).
      const dqVid = (!ogSubscribed && targetThreshold === 3)
        ? this.settings.threshold3DoubleQtyVid
        : ogAtT5
          ? this.settings.threshold5DoubleQtyVid
          : 0;
      if (dqVid) targetVIds.add(dqVid);

      // Double-qty product's current qty + line KEY (target by key so a same-variant _free_mask sample is never hit).
      const dqItem = (dqVid > 0 && cart)
        ? cart.items.find(item =>
              Number(item.variant_id) === dqVid &&
              item.properties && item.properties['_gift_product'] === 'true'
            )
        : null;
      const dqCurrentQty = dqItem?.quantity ?? 0;
      const dqKey = dqItem?.key || null;

      // Diff: gift variants in cart that aren't wanted → remove
      //       target products whose variant isn't in cart yet → add
      // dqVid is excluded from productsToAdd — it's handled with explicit qty 2
      // below so a concurrent regular-add (qty 1) doesn't race and leave qty 3.
        // Detect promo activity from BOTH the window flag (set by app after sync)
        // AND directly from cart discount codes + app config element presence.
        // This eliminates the race where the theme runs before the app sets the flag.
        const promoActive = window.__skiiPromoActive === true || this._isPromoActiveFromCart(cart);
      // When the promo app is active, the theme must not keep ANY of its own gifts —
      // clear targetVIds so all existing theme-managed gifts are removed and the promo
      // app has full control over which gifts are in the cart.
      if (promoActive) targetVIds.clear();
      const variantsToRemove = [...currentGiftVariants].filter(vid => !targetVIds.has(vid));
      const productsToAdd = promoActive ? [] : targetProducts.filter(p => {
        const vid = firstVariantId(p);
        return vid && vid !== dqVid && !currentGiftVariants.has(vid);
      });
      // needDoubleQty: dqVid product exists in cart but at wrong qty (0 = never added,
      // 1 = carried over from a lower threshold level — both need correction to 2).
      const needDoubleQty = !promoActive && dqVid > 0 && dqCurrentQty !== 2;

      // Qty downgrade: when leaving T5/T3 double-qty level the shared double-qty
      // product (FT Mask) may still be in cart at qty 2 but only needs qty 1
      // as a regular gift at the current level (e.g. T4 or T2). Detect and fix.
      const { threshold5DoubleQtyVid: t5dqv, threshold3DoubleQtyVid: t3dqv } = this.settings;
      const qtyDowngrades = []; // stores _gift_product line KEYS, not variant ids
      new Set([t5dqv, t3dqv].filter(v => v && v !== dqVid)).forEach(vid => {
        if (!targetVIds.has(vid)) return; // not a current target — variantsToRemove handles it
        const item = cart && cart.items
          ? cart.items.find(i =>
              Number(i.variant_id) === vid &&
              i.properties && i.properties['_gift_product'] === 'true')
          : null;
        if (item && item.quantity === 2) qtyDowngrades.push(item.key);
      });

      if (variantsToRemove.length === 0 && productsToAdd.length === 0 && !needDoubleQty && qtyDowngrades.length === 0) return;

      // Show spinner on checkout button while gifts are being modified.
      this.showCheckoutLoading();

      // 1. Remove gift DOM nodes immediately â€” instant visual, no waiting for API.
      variantsToRemove.forEach(vid => {
        document.querySelectorAll(`.cart--item[data-is-gift="true"][data-variant-id="${vid}"]`)
          .forEach(el => el.remove());
      });
      const remainingItems = document.querySelectorAll('.cart--body .cart--item');
      if (remainingItems.length === 0) {
        const bar = document.querySelector('[data-gift-progress-bar]');
        if (bar) bar.style.display = 'none';
        const viewport = document.querySelector('.layout--viewport');
        if (viewport) viewport.setAttribute('data-cart-empty', 'true');
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.setAttribute('data-cart-count', '0');
        });
      }

      // 3. Sync backend + re-render drawer in background (totals, count badge, etc.).
      // OPTIMIZATION: Set flag so the __skiiCartDrawerSafetyNet fetch wrapper (in cart.liquid)
      // skips its redundant section refresh for our /cart/add.js calls — we call
      // refreshCartSections() ourselves after all ops complete.
      window.__skiiGiftAddInProgress = true;
      const ops = [
        variantsToRemove.length > 0 ? this.removeFromCartBatch(this._giftLineKeys(cart, variantsToRemove)) : Promise.resolve(),
        productsToAdd.length > 0    ? this.addToCartBatch(productsToAdd)          : Promise.resolve(),
        // Double-qty: update existing item to qty 2 if already in cart, otherwise add fresh.
        needDoubleQty
          ? (dqCurrentQty > 0
              ? fetch('/cart/update.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ updates: { [dqKey || dqVid]: 2 } })
                }).catch(e => console.error('Gift Progress Bar: Error updating double qty gift:', e))
              : fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ items: [{ id: dqVid, quantity: 2, properties: { '_gift_product': 'true' } }] })
                }).catch(e => console.error('Gift Progress Bar: Error adding double qty gift:', e))
            )
          : Promise.resolve(),
        // Qty downgrade: reduce double-qty products from 2 → 1 when leaving double-qty level.
        ...qtyDowngrades.map(key =>
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: { [key]: 1 } })
          }).catch(e => console.error('Gift Progress Bar: Error reducing gift qty:', e))
        )
      ];
      await Promise.all(ops);
      window.__skiiGiftAddInProgress = false;
      // Invalidate cart cache after modifications so next handleCartUpdate gets fresh data.
      this._cartCache = null;
      await this.refreshCartSections();
    }

    async refreshCartSections() {
      // Blockshop's theme:cart:refresh handler does POST /cart/update.js with only
      // Fix: fetch the section HTML ourselves (with updates:{} to guarantee Shopify
      // processes the request and returns sections), then fire theme:cart:*:updateHtml
      // directly — replicating Cart.updateAllHtml without relying on the event chain.
      const cartUpdateUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_update)
        ? `${window.Shopify.routes.cart_update}.js`
        : '/cart/update.js';
      try {
        const resp = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // updates:{} — empty object tells Shopify to process the request and return sections
          // without modifying any line item quantities.
          body: JSON.stringify({ updates: {}, sections: ['cart-drawer', 'cart-desktop', 'cart-mobile'] })
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data && data.sections && typeof window.trigger === 'function') {
            // Stamp before firing — blocks the theme:cart:updated that Blockshop fires
            // after swapping section HTML, preventing a redundant /cart.js fetch.
            this._ownRefreshPending = true;
            // Replicate Cart.updateAllHtml: fire the specific updateHtml event for each
            Object.entries(data.sections).forEach(([key, html]) => {
              window.trigger(`theme:cart:${key.replace(/^cart-/, '')}:updateHtml`, {
                section: html,
                load_transitions: false
              });
            });
            // Sync header cart-count badge with true item total (includes gift/sample items).
            // refreshCartSections bypasses Cart.addItems/Cart.updateTotals, so the badge
            // would otherwise remain stale after gift products are added or removed.
            if (typeof data.item_count === 'number') {
              document.querySelectorAll('[data-cart-count]').forEach(el => {
                el.setAttribute('data-cart-count', data.item_count);
              });
            }
            return; // Blockshop will fire theme:cart:updated after swapping content
          }
        }
      } catch (e) {
        console.error('Gift Progress Bar: Section refresh failed:', e);
      }
      // Fallback: let Blockshop's handler attempt the refresh
      window.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    }

    async addToCartBatch(products) {
      try {
        const items = products.map(product => {
          if (!product?.variants?.[0]) {
            console.error('Gift Progress Bar: Cannot resolve variant for product', product);
            return null;
          }
          return { id: product.variants[0].id, quantity: 1, properties: { '_gift_product': 'true' } };
        }).filter(Boolean);

        if (!items.length) return;
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Failed to batch add gift products');
      } catch (error) {
        console.error('Error batch adding gifts to cart:', error);
      }
    }

    // Map gift variant ids to their _gift_product line keys (falls back to variant id) so a same-variant _free_mask sample is never touched.
    _giftLineKeys(cart, vids) {
      if (!cart || !cart.items) return vids;
      return vids.map(vid => {
        const item = cart.items.find(i =>
          Number(i.variant_id) === Number(vid) &&
          i.properties && i.properties['_gift_product'] === 'true');
        return item ? item.key : vid;
      });
    }

    async removeFromCartBatch(lineKeys) {
      try {
        if (!lineKeys.length) return;
        const updates = {};
        lineKeys.forEach(key => { updates[key] = 0; });
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
        if (!response.ok) throw new Error('Failed to batch remove gift products');
      } catch (error) {
        console.error('Error batch removing gifts from cart:', error);
      }
    }

    updateProgressBar(subtotal, threshold, ogSubscribed = false) {
      // Always re-query the fresh DOM element
      const bar = document.querySelector('[data-gift-progress-bar]');
      if (!bar) return;

      // Coupon-code promo active: hide progress bar for any promo (FAN, AWO, etc.).
      // When promo is removed/reset, existing cart refresh flow re-runs this method
      // and the bar is shown again via normal threshold logic below.
      if (window.__skiiPromoActive === true || this._isPromoActiveFromCart(this._lastCart) || this._isAwoActive(this._lastCart)) {
        bar.style.display = 'none';
        return;
      }

      const { threshold2Value, threshold3Value, threshold2Title, threshold3Title, threshold3NextLabel, threshold3ShortTitle, barMaxValue } = this.settings;
      const messageElement = bar.querySelector('.cart--gift-progress-message');
      const barElement = bar.querySelector('.cart--gift-progress-bar');

      let message = '';
      let width = 0;

      if (ogSubscribed) {
        // Ordergroove subscription active — hide the gift progress bar.
        // The bar is only visible for KCP / non-KCP (one-time purchase) flows.
        bar.style.display = 'none';
        return;
      } else if (threshold === 3) {
        message = `Congrats! You've Earned a ${threshold3ShortTitle}`;
        width = (subtotal / barMaxValue) * 100;
      } else if (threshold === 2) {
        const remaining = this.formatMoney(threshold3Value - subtotal);
        message = `You have Earned a ${threshold2Title}! Spend <strong>${remaining}</strong> More to Earn a ${threshold3NextLabel}!`;
        width = (subtotal / barMaxValue) * 100;
      } else if (threshold === 1) {
        // Pitera range ($150-$199): hide the progress bar for regular purchasers only.
        // OG subscribers are handled above and never reach this branch.
        bar.style.display = 'none';
        return;
      } else {
        const remaining = this.formatMoney(threshold2Value - subtotal);
        message = `<strong>${remaining}</strong> from Earning a FREE ${threshold2Title}`;
        width = (subtotal / barMaxValue) * 100;
      }

      const clampedWidth = Math.min(Math.max(width, 0), 100);

      bar.style.display = 'block';
      bar.setAttribute('data-current-threshold', threshold);
      if (messageElement) messageElement.innerHTML = message;
      if (barElement) {
        barElement.style.setProperty('--progress-percent', `${clampedWidth}%`);
        const fillElement = barElement.querySelector('.cart--gift-progress-fill');
        if (fillElement) fillElement.style.width = `${clampedWidth}%`;
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      // Use Shopify's money format if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
      }
      return `$${amount}`;
    }

  } // end class GiftProgressManager

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new GiftProgressManager());
  } else {
    new GiftProgressManager();
  }
})();
